import React, { useState } from 'react';
import { motion, AnimatePresence } from "framer-motion";
import Banner from '../Components/Banner';
import { ChevronRight } from 'lucide-react';
import { Helmet } from "react-helmet-async";
import LocationForm from '../Components/mumbai/LocationForm';
import ServiceNowLeadForm from '../Components/ServiceNowLeadForm';

function ServiceNowManagedServices() {
const servicenowFaqData = [
    {
        id: 1,
        question: "What are ServiceNow managed services?",
        answer:
            "ServiceNow managed services provide ongoing support, maintenance, administration, enhancements, integrations, and platform management for your ServiceNow environment."
    },
    {
        id: 2,
        question: "What is included in ServiceNow managed services?",
        answer:
            "Services can include platform administration, technical support, issue resolution, configuration, workflow automation, integrations, maintenance, upgrades, and testing."
    },
    {
        id: 3,
        question: "Can you provide ongoing ServiceNow support?",
        answer:
            "Yes. Mobikode provides ongoing ServiceNow support for businesses that need technical assistance, administration, troubleshooting, and platform improvements."
    },
    {
        id: 4,
        question: "Which ServiceNow modules can you support?",
        answer:
            "We support ITSM, ITOM, CMDB, CSM, HRSD, integrations, workflow automation, custom configurations, and other ServiceNow requirements."
    },
    {
        id: 5,
        question: "Can you support ServiceNow upgrades and integrations?",
        answer:
            "Yes. Our services can include ServiceNow upgrades, maintenance, testing, integrations, REST and SOAP APIs, IntegrationHub, and third-party application support."
    },
    {
        id: 6,
        question: "Why choose Mobikode for ServiceNow managed services?",
        answer:
            "Mobikode provides flexible ServiceNow support covering administration, maintenance, integrations, enhancements, automation, and ongoing technical requirements."
    }
];
          const [activeIndex, setActiveIndex] = useState(0);
          const [faqActiveIndex, setFaqActiveIndex] = useState(0);
    
    const toggleFaqAccordion = (index) => {
        setFaqActiveIndex(faqActiveIndex === index ? -1 : index);
    };
  return (
    
    <div>
<Helmet>
    {/* Primary SEO */}
    <title>
        ServiceNow Managed Services | Mobikode
    </title>

    <meta
        name="description"
        content="Get reliable ServiceNow managed services from Mobikode, including ongoing support, maintenance, administration, enhancements, integrations, upgrades, and platform management."
    />

    <meta
        name="keywords"
        content="ServiceNow managed services, ServiceNow support services, ServiceNow managed support, ServiceNow maintenance services, ServiceNow platform support, ServiceNow administration services, ServiceNow managed services company, ServiceNow ongoing support, ServiceNow support company, ServiceNow upgrade services, ServiceNow ITSM support, ServiceNow ITOM support"
    />

    <link
        rel="canonical"
        href="https://www.mobikode.com/servicenow-managed-services/"
    />


    {/* Open Graph */}
    <meta
        property="og:title"
        content="ServiceNow Managed Services | Mobikode"
    />

    <meta
        property="og:description"
        content="Keep your ServiceNow platform running efficiently with ongoing support, maintenance, administration, enhancements, integrations, upgrades, and platform management."
    />

    <meta
        property="og:url"
        content="https://www.mobikode.com/servicenow-managed-services/"
    />

    <meta
        property="og:type"
        content="website"
    />

    <meta
        property="og:site_name"
        content="Mobikode"
    />

    <meta
        property="og:image"
        content="https://www.mobikode.com/assets/services/servicenow-managed-services.jpg"
    />

    <meta
        property="og:image:alt"
        content="ServiceNow Managed Services by Mobikode"
    />


    {/* Twitter / X */}
    <meta
        name="twitter:card"
        content="summary_large_image"
    />

    <meta
        name="twitter:title"
        content="ServiceNow Managed Services | Mobikode"
    />

    <meta
        name="twitter:description"
        content="Mobikode provides ServiceNow managed services for ongoing support, maintenance, administration, integrations, enhancements, upgrades, and platform management."
    />

    <meta
        name="twitter:image"
        content="https://www.mobikode.com/assets/services/servicenow-managed-services.jpg"
    />

    <meta
        name="twitter:image:alt"
        content="ServiceNow Managed Services by Mobikode"
    />
</Helmet>


<section className="relative h-[500px] lg:h-[630px] w-full overflow-hidden bg-[#0a0b1e]">
    <div className="absolute inset-0 z-0">
        <img
            src="/assets/services/bg3.png"
            alt="ServiceNow managed services and ongoing platform support"
            className="h-full w-full object-cover opacity-40"
        />

        <div className="absolute inset-0 bg-gradient-to-b lg:bg-gradient-to-r from-[#0a0b1e] via-[#0a0b1e]/80 to-transparent opacity-90" />
    </div>

    <div className="relative z-10 flex h-full max-w-[1440px] w-full mx-auto flex-col justify-between px-6 lg:px-20 pt-20 pb-[60px]">

        {/* Hero Heading */}
        <motion.div
            initial={{ opacity: 0, x: -60 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, ease: "easeOut" }}
            viewport={{ once: true }}
            className="lg:mt-[115px] mt-[40px]"
        >
            <h1 className="text-[38px] lg:text-[55px] text-[#FFFFFF] max-w-[800px] w-full font-sora font-[600] leading-[1.2] mb-[22px]">
                ServiceNow Managed Services
            </h1>

            <p className="text-[#FFFFFF] font-graphik text-[16px] md:text-[20px] font-[400] leading-[1.6] mb-4">
                Ongoing ServiceNow Support, Maintenance, and Platform Management
            </p>
        </motion.div>

        {/* Hero Description */}
        <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.3 }}
            viewport={{ once: true }}
            className="mt-12 lg:self-end max-w-full lg:max-w-[501px] lg:ml-auto text-left md:mt-0"
        >
            <p className="text-[#FFFFFF] font-graphik text-[14px] md:text-[16px] font-[400] leading-[1.6]">
                Keep your ServiceNow platform running efficiently with ongoing
                support, maintenance, administration, enhancements, integrations,
                and platform management. Mobikode provides flexible ServiceNow
                managed services to help businesses maintain performance,
                resolve issues, and continuously improve their ServiceNow
                environment.
            </p>
        </motion.div>

    </div>
</section>


<section className="bg-[#FFFFFF] overflow-hidden lg:py-[100px] py-[60px]">
    <div className="max-w-[1440px] w-full mx-auto lg:px-[80px] px-[20px]">

        <div className="flex flex-col lg:flex-row items-center gap-[40px] lg:gap-[80px]">

            {/* LEFT IMAGE */}
            <motion.div
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
                className="w-full lg:w-1/2"
            >
                <div className="w-full h-[280px] md:h-[380px] lg:h-[450px] overflow-hidden">
                    <img
                        src="/assets/sales/img2.png"
                        alt="ServiceNow managed services and platform support"
                        className="w-full h-full object-cover transition-transform duration-700 hover:scale-105"
                    />
                </div>
            </motion.div>

            {/* RIGHT CONTENT */}
            <div className="w-full lg:w-1/2 text-left">

                <motion.div
                    initial={{ opacity: 0, y: -15 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    className="flex items-center gap-[10px] pb-[15px] lg:pb-[20px]"
                >
                    <motion.span className="font-[500] font-graphik text-[16px] lg:text-[20px] leading-[1.6] text-[#01386A]">
                        Service Overview
                    </motion.span>
                </motion.div>

                <motion.h2
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: 0.2 }}
                    className="font-[600] font-sora lg:text-[30px] max-w-[850px] w-full text-[20px] leading-[1.3] text-[#01386A]"
                >
                    Reliable ServiceNow Managed Services for Your Business
                </motion.h2>

                <motion.p
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: 0.35 }}
                    className="font-[400] max-w-[800px] w-full font-graphik text-[14px] lg:text-[16px] leading-[1.6] lg:leading-[22px] text-[#01386A] mt-[20px] lg:mt-[25px]"
                >
                    Mobikode provides ServiceNow managed services to help
                    businesses maintain, support, optimize, and continuously
                    improve their ServiceNow platform. Our services can cover
                    platform administration, issue resolution, configuration,
                    enhancements, integrations, workflow automation, and ongoing
                    technical support.

                    <br /><br />

                    Whether you need ongoing ServiceNow support after an
                    implementation or additional expertise to manage a growing
                    platform, our flexible managed services approach helps keep
                    your ServiceNow environment aligned with business needs,
                    operational requirements, and long-term platform goals.
                </motion.p>

                <motion.div
                    initial={{ width: 0 }}
                    whileInView={{ width: "70px" }}
                    viewport={{ once: true }}
                    transition={{ delay: 0.5, duration: 0.6 }}
                    className="h-[3px] bg-[#ED6800] mt-[25px] lg:mt-[35px]"
                />

            </div>

        </div>

    </div>
</section>

<section className="w-full bg-[#FDFDFD] py-16 lg:py-24 relative">
    <div className="max-w-[1440px] mx-auto px-[20px] lg:px-[80px] relative z-10">

        <div className="flex flex-col lg:flex-row gap-10 lg:gap-20 items-start">

            {/* LEFT SIDE */}
            <div className="w-full lg:w-[40%] lg:sticky lg:top-24">
                <h2 className="text-[#01386A] font-sora text-[20px] lg:text-[30px] font-[600] text-left">
                    ServiceNow Managed Services Expertise
                </h2>
            </div>


            {/* RIGHT SIDE */}
            <div className="w-full lg:w-[60%] flex flex-col gap-6">

                {/* Card 1 */}
                <div className="bg-white border border-[#E2E8F0] border-b-[4px] border-b-[#01386A] hover:border-b-[#e86c15] transition-colors duration-300 p-8 lg:px-10 lg:py-10 flex flex-col justify-center cursor-pointer group">
                    <h3 className="text-[#01386A] text-[16px] lg:text-[20px] font-[600] font-sora mb-2">
                        ServiceNow Platform Administration
                    </h3>
                    <p className="text-[#49577A] text-[14px] lg:text-[16px] font-[500] font-graphik leading-snug">
                        Keep your ServiceNow platform properly configured and
                        maintained with ongoing administration, user management,
                        access controls, workflows, configurations, and
                        day-to-day platform support.
                    </p>
                </div>


                {/* Card 2 */}
                <div className="bg-white border border-[#E2E8F0] border-b-[4px] border-b-[#01386A] hover:border-b-[#e86c15] transition-colors duration-300 p-8 lg:px-10 lg:py-10 flex flex-col justify-center cursor-pointer group">
                    <h3 className="text-[#01386A] text-[16px] lg:text-[20px] font-[600] font-sora mb-2">
                        ServiceNow Support & Issue Resolution
                    </h3>
                    <p className="text-[#49577A] text-[14px] lg:text-[16px] font-[500] font-graphik leading-snug">
                        Get ongoing technical support for ServiceNow issues,
                        configuration problems, workflow errors, platform
                        requests, and operational challenges affecting your
                        ServiceNow environment.
                    </p>
                </div>


                {/* Card 3 */}
                <div className="bg-white border border-[#E2E8F0] border-b-[4px] border-b-[#01386A] hover:border-b-[#e86c15] transition-colors duration-300 p-8 lg:px-10 lg:py-10 flex flex-col justify-center cursor-pointer group">
                    <h3 className="text-[#01386A] text-[16px] lg:text-[20px] font-[600] font-sora mb-2">
                        ServiceNow Enhancements & Optimization
                    </h3>
                    <p className="text-[#49577A] text-[14px] lg:text-[16px] font-[500] font-graphik leading-snug">
                        Continuously improve your ServiceNow platform through
                        configuration enhancements, workflow improvements,
                        automation, customizations, and optimization aligned with
                        changing business requirements.
                    </p>
                </div>


                {/* Card 4 */}
                <div className="bg-white border border-[#E2E8F0] border-b-[4px] border-b-[#01386A] hover:border-b-[#e86c15] transition-colors duration-300 p-8 lg:px-10 lg:py-10 flex flex-col justify-center cursor-pointer group">
                    <h3 className="text-[#01386A] text-[16px] lg:text-[20px] font-[600] font-sora mb-2">
                        ServiceNow Integration Support
                    </h3>
                    <p className="text-[#49577A] text-[14px] lg:text-[16px] font-[500] font-graphik leading-snug">
                        Maintain and enhance ServiceNow integrations with
                        enterprise applications through REST and SOAP APIs,
                        IntegrationHub, data synchronization, third-party
                        systems, and automated workflows.
                    </p>
                </div>


                {/* Card 5 */}
                <div className="bg-white border border-[#E2E8F0] border-b-[4px] border-b-[#01386A] hover:border-b-[#e86c15] transition-colors duration-300 p-8 lg:px-10 lg:py-10 flex flex-col justify-center cursor-pointer group">
                    <h3 className="text-[#01386A] text-[16px] lg:text-[20px] font-[600] font-sora mb-2">
                        ServiceNow Upgrades & Maintenance
                    </h3>
                    <p className="text-[#49577A] text-[14px] lg:text-[16px] font-[500] font-graphik leading-snug">
                        Support ServiceNow upgrades, maintenance activities,
                        testing, configuration reviews, compatibility checks,
                        and post-upgrade validation to help maintain a stable
                        platform environment.
                    </p>
                </div>


                {/* Card 6 */}
                <div className="bg-white border border-[#E2E8F0] border-b-[4px] border-b-[#01386A] hover:border-b-[#e86c15] transition-colors duration-300 p-8 lg:px-10 lg:py-10 flex flex-col justify-center cursor-pointer group">
                    <h3 className="text-[#01386A] text-[16px] lg:text-[20px] font-[600] font-sora mb-2">
                        ServiceNow Workflow & Automation Support
                    </h3>
                    <p className="text-[#49577A] text-[14px] lg:text-[16px] font-[500] font-graphik leading-snug">
                        Improve ServiceNow operations through workflow
                        automation, business rules, platform scripting, process
                        improvements, and ongoing enhancements that reduce
                        manual work and support efficient service delivery.
                    </p>
                </div>

            </div>

        </div>

    </div>
</section>

<section className="w-full bg-white py-16 lg:py-24">
    <div className="max-w-[1440px] mx-auto px-[20px] lg:px-[80px]">

        <h2 className="text-[#01386A] text-[20px] md:text-[30px] font-[600] font-sora mb-6 lg:mb-10 text-left">
            ServiceNow Managed Services Expertise
        </h2>

        <div className="flex flex-col lg:flex-row gap-12 lg:gap-24">

            {/* LEFT SIDE - TABS */}
            <div className="lg:w-1/2 flex flex-col">

                {/* ITSM SUPPORT */}
                <div
                    onClick={() => setActiveIndex(0)}
                    className={`group cursor-pointer flex items-center justify-between py-6 lg:py-8 border-b-[3px] transition-all duration-300 ${
                        activeIndex === 0
                            ? "border-[#ED6800]"
                            : "border-gray-300 hover:border-gray-600"
                    }`}
                >
                    <h3
                        className={`text-[16px] md:text-[20px] font-[600] font-sora transition-colors duration-300 ${
                            activeIndex === 0
                                ? "text-[#01386A]"
                                : "text-[#0000008C] group-hover:text-gray-600"
                        }`}
                    >
                        ServiceNow ITSM Support
                    </h3>

                    <ChevronRight
                        size={24}
                        className={`transition-all duration-300 ${
                            activeIndex === 0
                                ? "opacity-100 translate-x-0 text-[#ED6800]"
                                : "opacity-0 -translate-x-4 text-gray-300"
                        }`}
                    />
                </div>


                {/* ITOM & CMDB SUPPORT */}
                <div
                    onClick={() => setActiveIndex(1)}
                    className={`group cursor-pointer flex items-center justify-between py-6 lg:py-8 border-b-[3px] transition-all duration-300 ${
                        activeIndex === 1
                            ? "border-[#ED6800]"
                            : "border-gray-300 hover:border-gray-600"
                    }`}
                >
                    <h3
                        className={`text-[16px] md:text-[20px] font-[600] font-sora transition-colors duration-300 ${
                            activeIndex === 1
                                ? "text-[#01386A]"
                                : "text-[#0000008C] group-hover:text-gray-600"
                        }`}
                    >
                        ServiceNow ITOM & CMDB Support
                    </h3>

                    <ChevronRight
                        size={24}
                        className={`transition-all duration-300 ${
                            activeIndex === 1
                                ? "opacity-100 translate-x-0 text-[#ED6800]"
                                : "opacity-0 -translate-x-4 text-gray-300"
                        }`}
                    />
                </div>


                {/* CSM SUPPORT */}
                <div
                    onClick={() => setActiveIndex(2)}
                    className={`group cursor-pointer flex items-center justify-between py-6 lg:py-8 border-b-[3px] transition-all duration-300 ${
                        activeIndex === 2
                            ? "border-[#ED6800]"
                            : "border-gray-300 hover:border-gray-600"
                    }`}
                >
                    <h3
                        className={`text-[16px] md:text-[20px] font-[600] font-sora transition-colors duration-300 ${
                            activeIndex === 2
                                ? "text-[#01386A]"
                                : "text-[#0000008C] group-hover:text-gray-600"
                        }`}
                    >
                        ServiceNow CSM Support
                    </h3>

                    <ChevronRight
                        size={24}
                        className={`transition-all duration-300 ${
                            activeIndex === 2
                                ? "opacity-100 translate-x-0 text-[#ED6800]"
                                : "opacity-0 -translate-x-4 text-gray-300"
                        }`}
                    />
                </div>


                {/* HRSD SUPPORT */}
                <div
                    onClick={() => setActiveIndex(3)}
                    className={`group cursor-pointer flex items-center justify-between py-6 lg:py-8 border-b-[3px] transition-all duration-300 ${
                        activeIndex === 3
                            ? "border-[#ED6800]"
                            : "border-gray-300 hover:border-gray-600"
                    }`}
                >
                    <h3
                        className={`text-[16px] md:text-[20px] font-[600] font-sora transition-colors duration-300 ${
                            activeIndex === 3
                                ? "text-[#01386A]"
                                : "text-[#0000008C] group-hover:text-gray-600"
                        }`}
                    >
                        ServiceNow HRSD Support
                    </h3>

                    <ChevronRight
                        size={24}
                        className={`transition-all duration-300 ${
                            activeIndex === 3
                                ? "opacity-100 translate-x-0 text-[#ED6800]"
                                : "opacity-0 -translate-x-4 text-gray-300"
                        }`}
                    />
                </div>


                {/* INTEGRATION SUPPORT */}
                <div
                    onClick={() => setActiveIndex(4)}
                    className={`group cursor-pointer flex items-center justify-between py-6 lg:py-8 border-b-[3px] transition-all duration-300 ${
                        activeIndex === 4
                            ? "border-[#ED6800]"
                            : "border-gray-300 hover:border-gray-600"
                    }`}
                >
                    <h3
                        className={`text-[16px] md:text-[20px] font-[600] font-sora transition-colors duration-300 ${
                            activeIndex === 4
                                ? "text-[#01386A]"
                                : "text-[#0000008C] group-hover:text-gray-600"
                        }`}
                    >
                        ServiceNow Integration Support
                    </h3>

                    <ChevronRight
                        size={24}
                        className={`transition-all duration-300 ${
                            activeIndex === 4
                                ? "opacity-100 translate-x-0 text-[#ED6800]"
                                : "opacity-0 -translate-x-4 text-gray-300"
                        }`}
                    />
                </div>


                {/* PLATFORM ADMINISTRATION */}
                <div
                    onClick={() => setActiveIndex(5)}
                    className={`group cursor-pointer flex items-center justify-between py-6 lg:py-8 border-b-[3px] transition-all duration-300 ${
                        activeIndex === 5
                            ? "border-[#ED6800]"
                            : "border-gray-300 hover:border-gray-600"
                    }`}
                >
                    <h3
                        className={`text-[16px] md:text-[20px] font-[600] font-sora transition-colors duration-300 ${
                            activeIndex === 5
                                ? "text-[#01386A]"
                                : "text-[#0000008C] group-hover:text-gray-600"
                        }`}
                    >
                        ServiceNow Platform Administration
                    </h3>

                    <ChevronRight
                        size={24}
                        className={`transition-all duration-300 ${
                            activeIndex === 5
                                ? "opacity-100 translate-x-0 text-[#ED6800]"
                                : "opacity-0 -translate-x-4 text-gray-300"
                        }`}
                    />
                </div>

            </div>


            {/* RIGHT SIDE - CONTENT */}
            <div className="lg:w-1/2 flex flex-col justify-start">

                <div className="min-h-[100px] mb-8">

                    {/* ITSM SUPPORT */}
                    {activeIndex === 0 && (
                        <p className="text-[#49577A] text-[14px] lg:text-[16px] leading-relaxed font-[500] font-graphik animate-fade-in">
                            Support your ServiceNow ITSM environment with ongoing
                            incident, problem, change, request, and knowledge
                            management support. Our team can maintain workflows,
                            configurations, automation, and service management
                            processes while addressing ongoing platform
                            requirements.
                        </p>
                    )}


                    {/* ITOM & CMDB SUPPORT */}
                    {activeIndex === 1 && (
                        <p className="text-[#49577A] text-[14px] lg:text-[16px] leading-relaxed font-[500] font-graphik animate-fade-in">
                            Maintain ServiceNow ITOM and CMDB capabilities with
                            ongoing support for discovery, service mapping,
                            configuration management, event management,
                            infrastructure visibility, and operational workflows.
                            Our team can help maintain and enhance your existing
                            ServiceNow environment.
                        </p>
                    )}


                    {/* CSM SUPPORT */}
                    {activeIndex === 2 && (
                        <p className="text-[#49577A] text-[14px] lg:text-[16px] leading-relaxed font-[500] font-graphik animate-fade-in">
                            Support your ServiceNow CSM environment with ongoing
                            case management, customer workflows, portals,
                            automation, integrations, configuration updates, and
                            platform enhancements. Our team can help maintain and
                            improve customer service workflows over time.
                        </p>
                    )}


                    {/* HRSD SUPPORT */}
                    {activeIndex === 3 && (
                        <p className="text-[#49577A] text-[14px] lg:text-[16px] leading-relaxed font-[500] font-graphik animate-fade-in">
                            Keep ServiceNow HRSD workflows running effectively with
                            ongoing support for employee services, case management,
                            HR workflows, integrations, automation, configuration,
                            and platform enhancements.
                        </p>
                    )}


                    {/* INTEGRATION SUPPORT */}
                    {activeIndex === 4 && (
                        <p className="text-[#49577A] text-[14px] lg:text-[16px] leading-relaxed font-[500] font-graphik animate-fade-in">
                            Maintain and enhance ServiceNow integrations with
                            enterprise systems using REST and SOAP APIs,
                            IntegrationHub, data synchronization, and automated
                            workflows. Our managed services team can support
                            ongoing integration monitoring, troubleshooting, and
                            enhancement requirements.
                        </p>
                    )}


                    {/* PLATFORM ADMINISTRATION */}
                    {activeIndex === 5 && (
                        <p className="text-[#49577A] text-[14px] lg:text-[16px] leading-relaxed font-[500] font-graphik animate-fade-in">
                            Get ongoing ServiceNow platform administration covering
                            configuration, user management, access controls,
                            workflows, maintenance, enhancements, and operational
                            support. Our team helps keep your ServiceNow platform
                            aligned with changing business and technical
                            requirements.
                        </p>
                    )}

                </div>


                {/* IMAGE */}
                <div className="w-full h-[300px] lg:h-[378px] overflow-hidden relative">

                    <div className="absolute inset-0 bg-gradient-to-tr from-blue-100/50 to-transparent z-10 pointer-events-none"></div>


                    {/* ITSM */}
                    {activeIndex === 0 && (
                        <img
                            src="/assets/home-/ai-2.png"
                            alt="ServiceNow ITSM managed support services"
                            className="w-full h-full object-cover transition-opacity duration-500 animate-[fadeIn_0.5s_ease-in-out_forwards]"
                        />
                    )}


                    {/* ITOM & CMDB */}
                    {activeIndex === 1 && (
                        <img
                            src="/assets/home-/ai-3.png"
                            alt="ServiceNow ITOM and CMDB managed services"
                            className="w-full h-full object-cover transition-opacity duration-500 animate-[fadeIn_0.5s_ease-in-out_forwards]"
                        />
                    )}


                    {/* CSM */}
                    {activeIndex === 2 && (
                        <img
                            src="/assets/home-/ai-4.png"
                            alt="ServiceNow CSM managed support services"
                            className="w-full h-full object-cover transition-opacity duration-500 animate-[fadeIn_0.5s_ease-in-out_forwards]"
                        />
                    )}


                    {/* HRSD */}
                    {activeIndex === 3 && (
                        <img
                            src="/assets/home-/img1.png"
                            alt="ServiceNow HRSD managed services"
                            className="w-full h-full object-cover transition-opacity duration-500 animate-[fadeIn_0.5s_ease-in-out_forwards]"
                        />
                    )}


                    {/* INTEGRATIONS */}
                    {activeIndex === 4 && (
                        <img
                            src="/assets/home-/img2.png"
                            alt="ServiceNow integration managed services"
                            className="w-full h-full object-cover transition-opacity duration-500 animate-[fadeIn_0.5s_ease-in-out_forwards]"
                        />
                    )}


                    {/* PLATFORM ADMINISTRATION */}
                    {activeIndex === 5 && (
                        <img
                            src="/assets/home-/img3.png"
                            alt="ServiceNow platform administration services"
                            className="w-full h-full object-cover transition-opacity duration-500 animate-[fadeIn_0.5s_ease-in-out_forwards]"
                        />
                    )}

                </div>

            </div>

        </div>
    </div>


    <style>{`
        @keyframes fadeIn {
            from {
                opacity: 0;
                transform: translateY(10px);
            }

            to {
                opacity: 1;
                transform: translateY(0);
            }
        }

        .animate-fade-in {
            animation: fadeIn 0.5s ease-out forwards;
        }
    `}</style>

</section>

<section className="w-full pb-[40px] md:pb-[100px] bg-white">
    <div className="max-w-[1440px] mx-auto px-[20px] md:px-[40px] lg:px-[80px]">

        <div className="flex flex-col lg:flex-row items-start gap-[24px] md:gap-[40px] lg:gap-[80px]">

            {/* LEFT SIDE */}
            <motion.div
                initial={{ opacity: 0, y: -20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{
                    duration: 0.8,
                    ease: "easeOut"
                }}
                className="w-full lg:w-5/12 lg:sticky lg:top-[120px]"
            >
                <h2 className="font-sora text-[20px] lg:text-[30px] font-[600] text-[#01386A] tracking-tight leading-[1.2] lg:max-w-[350px]">
                    Frequently Asked Questions About ServiceNow Staff Augmentation
                </h2>
            </motion.div>


            {/* RIGHT SIDE */}
            <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{
                    once: true,
                    amount: 0.1
                }}
                transition={{
                    duration: 0.6,
                    ease: "easeOut"
                }}
                className="w-full lg:w-7/12 flex flex-col border border-[#E4E8F5] bg-white rounded-sm"
            >

                {servicenowFaqData.map((faq, index) => {

                    const isActive = faqActiveIndex === index;

                    return (
                        <div
                            key={faq.id}
                            className={`flex flex-col border-b ${
                                isActive
                                    ? "border-[#ED6800] border-b-[3px] md:border-b-[4px]"
                                    : "border-[#E4E8F5]"
                            } last:border-b-0 transition-colors duration-300`}
                        >

                            {/* QUESTION */}
                            <button
                                type="button"
                                onClick={() => toggleFaqAccordion(index)}
                                className="w-full flex justify-between items-center text-left py-[18px] sm:py-[24px] md:py-[32px] px-[16px] sm:px-[24px] md:px-[40px] group cursor-pointer bg-white md:hover:bg-[#F8FAFF] transition-colors duration-300"
                                aria-expanded={isActive}
                            >

                                <h3
                                    className={`font-sora max-w-[85%] text-[16px] md:text-[20px] font-[600] pr-[16px] sm:pr-[20px] transition-colors duration-300 ${
                                        isActive
                                            ? "text-[#01386A]"
                                            : "text-[#01386A] md:group-hover:text-[#ED6800]"
                                    }`}
                                >
                                    {faq.question}
                                </h3>


                                {/* PLUS / MINUS ICON */}
                                <div className="flex-shrink-0 text-[#01386A] transition-colors duration-300 md:group-hover:text-[#ED6800]">

                                    {isActive ? (

                                        <svg
                                            width="18"
                                            height="18"
                                            className="md:w-[20px] md:h-[20px]"
                                            viewBox="0 0 24 24"
                                            fill="none"
                                            stroke="currentColor"
                                            strokeWidth="2.5"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            aria-hidden="true"
                                        >
                                            <line
                                                x1="5"
                                                y1="12"
                                                x2="19"
                                                y2="12"
                                            />
                                        </svg>

                                    ) : (

                                        <svg
                                            width="18"
                                            height="18"
                                            className="md:w-[20px] md:h-[20px]"
                                            viewBox="0 0 24 24"
                                            fill="none"
                                            stroke="currentColor"
                                            strokeWidth="2.5"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            aria-hidden="true"
                                        >
                                            <line
                                                x1="12"
                                                y1="5"
                                                x2="12"
                                                y2="19"
                                            />

                                            <line
                                                x1="5"
                                                y1="12"
                                                x2="19"
                                                y2="12"
                                            />
                                        </svg>

                                    )}

                                </div>

                            </button>


                            {/* ANSWER */}
                            <AnimatePresence initial={false}>

                                {isActive && (

                                    <motion.div
                                        initial={{
                                            height: 0,
                                            opacity: 0
                                        }}
                                        animate={{
                                            height: "auto",
                                            opacity: 1
                                        }}
                                        exit={{
                                            height: 0,
                                            opacity: 0
                                        }}
                                        transition={{
                                            duration: 0.4,
                                            ease: "easeInOut"
                                        }}
                                        className="overflow-hidden bg-white"
                                    >

                                        <div className="pb-[20px] sm:pb-[32px] px-[16px] sm:px-[24px] md:px-[40px]">

                                            <p className="font-graphik text-[#6C7689] text-[14px] md:text-[16px] font-[500] leading-[1.6]">
                                                {faq.answer}
                                            </p>

                                        </div>

                                    </motion.div>

                                )}

                            </AnimatePresence>

                        </div>
                    );
                })}

            </motion.div>

        </div>

    </div>
</section>

  <ServiceNowLeadForm/>
<Banner
    title="ServiceNow Managed Services"
    text="Keep your ServiceNow platform running efficiently with ongoing support, maintenance, administration, enhancements, integrations, upgrades, and platform management aligned with your business requirements."
/>

    </div>
  )
}


export default ServiceNowManagedServices
